import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { F0Dialog, F0Select, F0Text } from '@factorialco/f0-react'
import { Input, Stack, Textarea } from '@factorialco/f0-react/dist/experimental'
import {
  CreateTicketingTicketCategoryDocument,
  UpdateTicketingTicketCategoryDocument,
  TicketingTicketCategoriesDocument,
  TicketingResolverTeamsDocument,
  TicketingTeamMembersDocument,
  type TicketingResolverTeamsQuery,
  type TicketingTeamMembersQuery,
  type TicketingTeamMembersQueryVariables,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import PriorityConfig from './PriorityConfig'
import SlaConfig from './SlaConfig'

interface CategoryData {
  id?: string
  name: string
  description: string | null
  resolverTeamId: string
  assignmentMode: string
  defaultAssigneeId: string | null
  priorityMode: string
  fixedPriorityLevel: string | null
  slaMode: string
  slaFixedHours: number | null
  slaCriticalHours: number | null
  slaHighHours: number | null
  slaMediumHours: number | null
  slaLowHours: number | null
  pauseSlaOnWaiting: boolean
}

interface Props {
  category?: CategoryData | null
  onClose: () => void
}

const DEFAULT_FORM: CategoryData = {
  name: '',
  description: null,
  resolverTeamId: '',
  assignmentMode: 'unassigned',
  defaultAssigneeId: null,
  priorityMode: 'no_priority',
  fixedPriorityLevel: null,
  slaMode: 'no_sla',
  slaFixedHours: null,
  slaCriticalHours: null,
  slaHighHours: null,
  slaMediumHours: null,
  slaLowHours: null,
  pauseSlaOnWaiting: false,
}

const CategoryFormDialog = ({ category, onClose }: Props) => {
  const isEditing = !!category?.id
  const [form, setForm] = useState<CategoryData>(category ?? DEFAULT_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: resolverTeamsData } = useQuery<TicketingResolverTeamsQuery, Record<string, never>>(
    TicketingResolverTeamsDocument
  )

  // Find the org team id for the selected resolver team
  const selectedOrgTeamId = useMemo(() => {
    if (!form.resolverTeamId || !resolverTeamsData) return null
    const rt = resolverTeamsData.ticketing.resolverTeamsConnection.nodes.find(
      (t) => String(t.id) === form.resolverTeamId
    )
    return rt ? String(rt.teamId) : null
  }, [form.resolverTeamId, resolverTeamsData])

  // Fetch members of the selected org team
  const { data: membersData } = useQuery<
    TicketingTeamMembersQuery,
    TicketingTeamMembersQueryVariables
  >(TicketingTeamMembersDocument, {
    variables: { teamIds: selectedOrgTeamId ? [selectedOrgTeamId] : [] },
    skip: !selectedOrgTeamId,
  })

  const resolverTeamOptions = useMemo(
    () =>
      (resolverTeamsData?.ticketing?.resolverTeamsConnection?.nodes ?? []).map((t) => ({
        value: String(t.id),
        label: t.teamName,
        avatar: { type: 'team' as const, name: t.teamName, src: '' },
      })),
    [resolverTeamsData]
  )

  const assignmentModeOptions = useMemo(
    () => [
      {
        value: 'unassigned',
        label: i18n.t('ticketing.settings.categories.form.assignment_modes.unassigned'),
      },
      {
        value: 'lowest_workload',
        label: i18n.t('ticketing.settings.categories.form.assignment_modes.lowest_workload'),
      },
      {
        value: 'specific_member',
        label: i18n.t('ticketing.settings.categories.form.assignment_modes.specific_member'),
      },
    ],
    []
  )

  const memberOptions = useMemo(() => {
    const members = membersData?.teams?.memberships ?? []
    return members
      .filter((m): m is typeof m & { employeeName: NonNullable<(typeof m)['employeeName']> } =>
        Boolean(m.employeeName)
      )
      .map((m) => ({
        value: String(m.employeeName.id),
        label: m.employeeName.fullName,
        avatar: {
          type: 'person' as const,
          firstName: m.employeeName.firstName,
          lastName: m.employeeName.lastName,
          src: '',
        },
      }))
  }, [membersData])

  // eslint-disable-next-line factorial/no-boolean-null
  const updateField = useCallback((field: string, value: string | number | boolean | null) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }, [])

  // Reset assignee when resolver team changes
  const initialResolverTeamId = useRef(category?.resolverTeamId)
  useEffect(() => {
    if (form.resolverTeamId && form.resolverTeamId !== initialResolverTeamId.current) {
      setForm((prev) => ({
        ...prev,
        assignmentMode: 'unassigned',
        defaultAssigneeId: null,
      }))
    }
  }, [form.resolverTeamId])

  // When priority mode changes to no_priority and sla_mode is sla_per_priority, reset
  useEffect(() => {
    if (form.priorityMode === 'no_priority' && form.slaMode === 'sla_per_priority') {
      setForm((prev) => ({ ...prev, slaMode: 'no_sla' }))
    }
  }, [form.priorityMode])

  const [performCreate] = useMutation(CreateTicketingTicketCategoryDocument, {
    extractorFn: (data) => data.ticketingMutations.createTicketingTicketCategory,
    refetchQueries: [TicketingTicketCategoriesDocument],
  })

  const [performUpdate] = useMutation(UpdateTicketingTicketCategoryDocument, {
    extractorFn: (data) => data.ticketingMutations.updateTicketingTicketCategory,
    refetchQueries: [TicketingTicketCategoriesDocument],
  })

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError(i18n.t('ticketing.settings.categories.validation.name_required'))
      return
    }
    if (!form.resolverTeamId) {
      setError(i18n.t('ticketing.settings.categories.validation.resolver_team_required'))
      return
    }

    setSubmitting(true)
    setError(null)

    const variables = {
      name: form.name.trim(),
      description: form.description?.trim() || null,
      resolverTeamId: form.resolverTeamId,
      assignmentMode: form.assignmentMode,
      defaultAssigneeId: form.defaultAssigneeId,
      priorityMode: form.priorityMode,
      fixedPriorityLevel: form.fixedPriorityLevel,
      slaMode: form.slaMode,
      slaFixedHours: form.slaFixedHours,
      slaCriticalHours: form.slaCriticalHours,
      slaHighHours: form.slaHighHours,
      slaMediumHours: form.slaMediumHours,
      slaLowHours: form.slaLowHours,
      pauseSlaOnWaiting: form.pauseSlaOnWaiting,
    }

    let handled
    if (isEditing) {
      const result = await performUpdate({ variables: { id: String(category?.id), ...variables } })
      handled = handleResult(result, () => {})
    } else {
      const result = await performCreate({ variables })
      handled = handleResult(result, () => {})
    }
    setSubmitting(false)

    if (handled.success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
      onClose()
    } else {
      const errors = handled.errors ?? []
      // String-match on Rails uniqueness error ('has already been taken') to detect duplicate names.
      // Rails uniqueness messages are stable and unlikely to change, so this is acceptable for now.
      // TODO: migrate to StructuredError field check (field === 'name') if name errors become
      // structured in the backend.
      const hasNameError = errors.some((e) => {
        if ('field' in e && e.field === 'name') return true
        if ('messages' in e) return e.messages?.some((m: string) => m.toLowerCase().includes('taken') || m.toLowerCase().includes('already'))
        if ('message' in e) return e.message?.toLowerCase().includes('name') && (e.message?.toLowerCase().includes('taken') || e.message?.toLowerCase().includes('already'))
        return false
      })
      setError(
        hasNameError
          ? i18n.t('ticketing.settings.categories.validation.name_taken')
          : i18n.t('errors.generic_error')
      )
    }
  }

  const handleAssignmentModeChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      assignmentMode: value,
      defaultAssigneeId: value === 'specific_member' ? prev.defaultAssigneeId : null,
    }))
  }

  const handleMemberChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      defaultAssigneeId: value,
    }))
  }

  const isSlaValid = useMemo(() => {
    if (form.slaMode === 'fixed_sla') {
      return form.slaFixedHours != null && form.slaFixedHours > 0
    }
    if (form.slaMode === 'sla_per_priority') {
      return (
        form.slaCriticalHours != null && form.slaCriticalHours > 0 &&
        form.slaHighHours != null && form.slaHighHours > 0 &&
        form.slaMediumHours != null && form.slaMediumHours > 0 &&
        form.slaLowHours != null && form.slaLowHours > 0
      )
    }
    return true
  }, [form.slaMode, form.slaFixedHours, form.slaCriticalHours, form.slaHighHours, form.slaMediumHours, form.slaLowHours])

  return (
    <F0Dialog
      isOpen
      width="md"
      title={
        isEditing
          ? i18n.t('ticketing.settings.categories.edit_title')
          : i18n.t('ticketing.settings.categories.create_title')
      }
      onClose={onClose}
      primaryAction={{
        label: isEditing ? i18n.t('common.save') : i18n.t('common.create'),
        onClick: handleSubmit,
        disabled: !form.name.trim() || !form.resolverTeamId || !isSlaValid || submitting,
        loading: submitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Stack paddingX="p-4" paddingY="p-4" gap="4">
        <Input
          label={i18n.t('ticketing.settings.categories.form.name')}
          value={form.name}
          onChange={(value) => updateField('name', value)}
          placeholder={i18n.t('ticketing.settings.categories.form.name_placeholder')}
          required
        />

        <Textarea
          label={i18n.t('ticketing.settings.categories.form.description')}
          value={form.description ?? ''}
          onChange={(value) => updateField('description', value || null)}
          placeholder={i18n.t('ticketing.settings.categories.form.description_placeholder')}
          rows={3}
        />

        <F0Select
          label={i18n.t('ticketing.settings.categories.form.resolver_team')}
          value={form.resolverTeamId}
          onChange={(value: string) => updateField('resolverTeamId', value)}
          options={resolverTeamOptions}
          placeholder={i18n.t('ticketing.settings.categories.form.resolver_team_placeholder')}
          required
          searchable
          showSearchBox
        />

        {form.resolverTeamId && (
          <>
            <F0Select
              label={i18n.t('ticketing.settings.categories.form.assignment_mode')}
              value={form.assignmentMode}
              onChange={handleAssignmentModeChange}
              options={assignmentModeOptions}
            />

            {form.assignmentMode === 'specific_member' && (
              <F0Select
                label={i18n.t('ticketing.settings.categories.form.default_assignee')}
                value={form.defaultAssigneeId ?? ''}
                onChange={handleMemberChange}
                options={memberOptions}
                placeholder={i18n.t(
                  'ticketing.settings.categories.form.default_assignee_placeholder'
                )}
                required
                searchable
                showSearchBox
              />
            )}
          </>
        )}

        <PriorityConfig
          priorityMode={form.priorityMode}
          fixedPriorityLevel={form.fixedPriorityLevel}
          onChange={updateField}
        />

        <SlaConfig
          priorityMode={form.priorityMode}
          slaMode={form.slaMode}
          slaFixedHours={form.slaFixedHours}
          slaCriticalHours={form.slaCriticalHours}
          slaHighHours={form.slaHighHours}
          slaMediumHours={form.slaMediumHours}
          slaLowHours={form.slaLowHours}
          pauseSlaOnWaiting={form.pauseSlaOnWaiting}
          onChange={updateField}
        />

        {error && <F0Text content={error} color="critical" />}
      </Stack>
    </F0Dialog>
  )
}

export default CategoryFormDialog
